import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { PdpEffectiveProductVm } from '../viewModels/pdp.vm';

/**
 * ViewContent payload shape (used for eventlogger + googleEcommStr).
 * FB ViewContent pixel/server events are mapped from `PDP Page Viewed` by FacebookAdapter.
 */
export interface ViewContentPayload {
  contentType: string;
  contentName: string;
  contentCategory: string;
  contentIds: string[];
  contents: Array<{ id: number; quantity: number }>;
  value: string;
}

declare global {
  interface Window {
    PAGE_TYPE?: string;
    viewContentPayload?: ViewContentPayload;
    googleEcommStr?: {
      ecomm_pagetype: string;
      ecomm_prodid: string[];
      ecomm_totalvalue: string;
    };
    dataLayer?: unknown[];
  }
}

/** Digit-only price string for FB ViewContent / eventlogger (legacy parity). */
export function formatViewContentPrice(discountedPrice: number | null | undefined): string {
  if (discountedPrice == null || !Number.isFinite(discountedPrice)) {
    return '';
  }
  return String(Math.round(discountedPrice));
}

/** Legacy eventlogger size padding (e.g. `7` → `07`, missing → `00`). */
export function formatEventLoggerSize(size: string | null | undefined): string {
  if (!size) {
    return '00';
  }
  return size.length === 1 ? `0${size}` : size;
}

/** Mirrors old-react `viewContentEventPayload(props)`. */
export function viewContentEventPayload(
  product: ProductDetail,
  discountedPrice: number | null | undefined,
  pageType = 'product',
): ViewContentPayload {
  return {
    contentType: pageType,
    contentName: product.designName ?? '',
    contentCategory: product.category ?? '',
    contentIds: [String(product.designId)],
    contents: [{ id: product.designId, quantity: 1 }],
    value: formatViewContentPrice(discountedPrice),
  };
}

/** @deprecated Use viewContentEventPayload */
export const buildViewContentPayload = viewContentEventPayload;

/** Sets GTM globals for Google remarketing (googleEcommStr). FB ViewContent is not set here. */
export function setPdpGtmViewContentGlobals(
  product: ProductDetail,
  discountedPrice: number | null | undefined,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.PAGE_TYPE = 'product';
  const payload = viewContentEventPayload(product, discountedPrice);

  // Intentionally omit window.viewContentPayload — the legacy GTM FB tag reads it and
  // would fire a duplicate ViewContent. Meta ViewContent is mapped from PDP Page Viewed.

  if (Array.isArray(window.dataLayer)) {
    window.googleEcommStr = {
      ecomm_pagetype: payload.contentType,
      ecomm_prodid: payload.contentIds,
      ecomm_totalvalue: payload.value,
    };
  }
}

export function clearPdpGtmViewContentGlobals(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.PAGE_TYPE = '';
  window.viewContentPayload = undefined;
  window.googleEcommStr = undefined;
}

export function buildProductViewedEventLoggerPayload(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
): Record<string, string | number> {
  const viewContent = viewContentEventPayload(product, vm.discountedPrice);

  return {
    _eventId: 'product_viewed',
    id: vm.activeCustomizationId,
    designId: product.designId,
    pageType: 'product',
    size: formatEventLoggerSize(product.size),
    contents: JSON.stringify(viewContent.contents),
    ids: JSON.stringify(viewContent.contentIds),
    productCategory: viewContent.contentCategory,
    productName: viewContent.contentName,
    content_type: viewContent.contentType,
    price: viewContent.value,
  };
}
